import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiJson } from "../../utils/api.js";
import { fmtDt, reportDefaultDateRange } from "../../utils/users.js";

const ACTION_ICONS = {
  assign: "👤",
  assign_source: "🔗",
  transfer: "🔁",
  transfer_user_to_manager: "🔁",
  transfer_deal_to_manager: "🔁",
  auto_reassign_overdue_op_new: "⏱",
  buyout_funnel_weekly_reminder: "📋",
  deal_auto_stage: "📈",
  deal_task_done: "✅",
  deal_call: "📞",
  comment_add: "💬",
  status_change: "📊",
  conversation_messages_only: "💬",
};

const ACTION_TONE = {
  assign: "tone-blue",
  assign_source: "tone-blue",
  transfer: "tone-purple",
  transfer_user_to_manager: "tone-purple",
  transfer_deal_to_manager: "tone-purple",
  auto_reassign_overdue_op_new: "tone-amber",
  buyout_funnel_weekly_reminder: "tone-amber",
  deal_auto_stage: "tone-cyan",
  deal_task_done: "tone-green",
  deal_call: "tone-teal",
  comment_add: "tone-slate",
  status_change: "tone-slate",
  conversation_messages_only: "tone-cyan",
};

function personLabel(p) {
  if (!p) return "—";
  const name = (p.name || "").trim();
  const uname = (p.username || "").trim();
  if (name && uname) return `${name} (@${uname})`;
  if (name) return name;
  if (uname) return `@${uname}`;
  if (p.telegram_id) return `id=${p.telegram_id}`;
  return "—";
}

function highlight(text, q) {
  if (!text) return null;
  const query = (q || "").trim();
  if (!query) return text;
  try {
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
    const parts = String(text).split(re);
    return parts.map((part, i) =>
      re.test(part) ? (
        <mark key={i} className="auditHighlight">
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  } catch {
    return text;
  }
}

function contentTypeLabel(t) {
  const map = {
    text: "текст",
    photo: "фото",
    video: "видео",
    voice: "аудио",
    audio: "аудио",
    document: "файл",
    sticker: "стикер",
    animation: "GIF",
    video_note: "кружок",
    location: "локация",
    contact: "контакт",
  };
  return map[t] || t || "—";
}

export function AuditLogsPanel() {
  const defaultRange = useMemo(() => reportDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);

  const [auditActionLabels, setAuditActionLabels] = useState({});
  const [stageTitles, setStageTitles] = useState({});

  const [auditAction, setAuditAction] = useState("");
  const [auditQ, setAuditQ] = useState("");
  const [messagesQ, setMessagesQ] = useState("");
  const [messagesQApplied, setMessagesQApplied] = useState("");

  const [auditTotal, setAuditTotal] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditLimit] = useState(100);
  const [auditItems, setAuditItems] = useState([]);
  const auditItemsRef = useRef([]);
  useEffect(() => {
    auditItemsRef.current = auditItems;
  }, [auditItems]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadAudit = useCallback(
    async ({ append }) => {
      const offset = append ? auditItemsRef.current.length : 0;
      const qs = new URLSearchParams();
      qs.set("date_from", dateFrom);
      qs.set("date_to", dateTo);
      qs.set("audit_only", "1");
      qs.set("include_audit", "true");
      qs.set("audit_limit", String(auditLimit));
      qs.set("audit_offset", String(offset));
      if (auditAction) qs.set("audit_action", auditAction);
      if ((auditQ || "").trim()) qs.set("audit_q", auditQ.trim());
      if ((messagesQ || "").trim()) qs.set("messages_q", messagesQ.trim());

      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await apiJson(`/api/reports?${qs.toString()}`);
        setAuditActionLabels(data.audit_action_labels ?? {});
        setStageTitles(data.stage_titles ?? {});
        const audit = data.audit ?? {};
        const items = audit.items ?? [];
        setAuditTotal(Number(audit.total) || 0);
        setAuditHasMore(!!audit.has_more);
        if (append) {
          setAuditItems((prev) => [...prev, ...items]);
        } else {
          setAuditItems(items);
          setMessagesQApplied((messagesQ || "").trim());
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [auditAction, auditLimit, auditQ, messagesQ, dateFrom, dateTo]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadAudit({ append: false });
      } catch {
        if (!cancelled) setAuditItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAudit]);

  const auditActionOptions = useMemo(() => {
    const labels = auditActionLabels || {};
    return Object.keys(labels)
      .sort()
      .map((k) => ({ value: k, label: labels[k] || k }));
  }, [auditActionLabels]);

  const renderAuditTitle = useCallback(
    (a) => {
      const action = a?.action || "";
      const meta = a?.meta || {};
      const labels = auditActionLabels || {};
      if (action === "deal_auto_stage") {
        const dealId = meta.deal_id;
        const fromId = meta.from_stage_id;
        const toId = meta.to_stage_id;
        const fromTitle = stageTitles?.[fromId] || fromId || "—";
        const toTitle = stageTitles?.[toId] || toId || "—";
        return `Сделка #${dealId}: ${fromTitle} → ${toTitle}`;
      }
      if (action === "deal_task_done") {
        const dealId = meta.deal_id;
        const title = meta.task_title || "—";
        return `Сделка #${dealId}: выполнено «${title}»`;
      }
      if (action === "deal_call") {
        const dealId = meta.deal_id;
        const dur = meta.duration_sec;
        const durTxt =
          typeof dur === "number" && dur > 0
            ? ` • ${Math.floor(dur / 60)}:${String(dur % 60).padStart(2, "0")}`
            : "";
        return `Сделка #${dealId}: звонок${durTxt}`;
      }
      if (action === "conversation_messages_only") {
        return labels[action] || "Переписка";
      }
      return labels[action] || action || "—";
    },
    [auditActionLabels, stageTitles]
  );

  const remaining = Math.max(0, auditTotal - auditItems.length);

  return (
    <div className="auditSurface">
      <div className="reportsPeriodRow" style={{ marginBottom: 12 }}>
        <input
          type="date"
          className="reportsPeriodDate input"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          max={dateTo}
        />
        <span className="reportsPeriodSep">—</span>
        <input
          type="date"
          className="reportsPeriodDate input"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          min={dateFrom}
        />
      </div>
      <div className="auditFiltersRow">
        <select
          className="input auditFilterSelect"
          value={auditAction}
          onChange={(e) => setAuditAction(e.target.value)}
        >
          <option value="">Все события</option>
          {auditActionOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          className="input auditFilterInput"
          placeholder="Telegram ID, id клиента или сделки"
          value={auditQ}
          onChange={(e) => setAuditQ(e.target.value)}
        />
        <input
          className="input auditFilterInput"
          placeholder="Поиск по сообщениям…"
          value={messagesQ}
          onChange={(e) => setMessagesQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") loadAudit({ append: false });
          }}
        />
        <button
          type="button"
          className="linkBtn auditFilterBtn"
          onClick={() => loadAudit({ append: false })}
          disabled={loading || loadingMore}
          title="Применить фильтры"
        >
          Применить
        </button>
      </div>

      <div className="auditSummary">
        {loading ? "Загрузка…" : `Найдено: ${auditTotal}`}
      </div>

      <div id="auditList" className="auditList">
        {auditItems.length ? (
          auditItems.map((a) => {
            const tone = ACTION_TONE[a.action] || "tone-slate";
            const icon = ACTION_ICONS[a.action] || "•";
            const msgs = Array.isArray(a.recent_messages) ? a.recent_messages : [];
            return (
              <div
                className={`auditCard ${tone}`}
                key={a.id != null ? String(a.id) : `syn-${a.target_user_telegram_id ?? "x"}`}
              >
                <div className="auditCardHead">
                  <span className="auditCardIcon" aria-hidden>
                    {icon}
                  </span>
                  <div className="auditCardHeadMain">
                    <div className="auditCardTitle">{renderAuditTitle(a)}</div>
                    <div className="auditCardSub">
                      <span className="auditCardDate">{fmtDt(a.created_at)}</span>
                      <span className="auditCardDot">·</span>
                      <span className="auditCardActor">Автор: {a.actor_display || "—"}</span>
                      {a.target_user_telegram_id ? (
                        <>
                          <span className="auditCardDot">·</span>
                          <span className="auditCardTarget">
                            user={a.target_user_telegram_id}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                {msgs.length ? (
                  <details
                    className="auditDetails"
                    open={
                      !!messagesQApplied || a.action === "conversation_messages_only"
                    }
                  >
                    <summary>
                      Сообщения ({msgs.length}
                      {messagesQApplied ? `, по запросу «${messagesQApplied}»` : ""})
                    </summary>
                    <ul className="auditMessagesList">
                      {msgs.map((m) => {
                        const isIn = m.direction === "client_to_manager";
                        return (
                          <li
                            key={m.id}
                            className={`auditMessageItem ${isIn ? "msgIn" : "msgOut"}`}
                          >
                            <div className="auditMessageHead">
                              <span className="auditMessageFrom" title="Отправитель">
                                {personLabel(m.from)}
                              </span>
                              <span className="auditMessageArrow">→</span>
                              <span className="auditMessageTo" title="Получатель">
                                {personLabel(m.to)}
                              </span>
                            </div>
                            <div className="auditMessageMeta">
                              {fmtDt(m.created_at)} · {contentTypeLabel(m.content_type)}
                            </div>
                            {m.text ? (
                              <div className="auditMessageText">
                                {highlight(m.text, messagesQApplied)}
                              </div>
                            ) : null}
                            {m.caption ? (
                              <div className="auditMessageCaption">
                                {highlight(m.caption, messagesQApplied)}
                              </div>
                            ) : null}
                            {!m.text && !m.caption ? (
                              <div className="auditMessageText muted">(без текста)</div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                ) : a.target_user_telegram_id ? (
                  <div className="auditCardHint muted">
                    {messagesQApplied
                      ? "По этому клиенту нет сообщений с совпадениями"
                      : "Нет сообщений в истории"}
                  </div>
                ) : null}

                {a.meta ? (
                  <details className="auditDetails">
                    <summary>meta</summary>
                    <pre className="auditMetaPre">{JSON.stringify(a.meta, null, 2)}</pre>
                  </details>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="auditEmpty">{loading ? "Загрузка…" : "Нет записей за период"}</div>
        )}

        {auditHasMore ? (
          <div className="auditLoadMore">
            <button
              type="button"
              className="linkBtn"
              onClick={() => loadAudit({ append: true })}
              disabled={loadingMore}
            >
              {loadingMore ? "Загрузка…" : remaining > 0 ? `Ещё (${remaining})` : "Ещё"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
